import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Check, ChevronRight, CheckCircleOutline, Login, Logout } from "@mui/icons-material";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import useInterfaceConfig from "@hooks/useInterfaceConfigV2";
import useIsAuthenticated from "@hooks/useIsAuthenticated";
import useSignOut from "@hooks/useSignOut";
import { getStaticResourceUrl } from "@helpers/funciones";
import { PATHS_AUTH_V2 } from "@helpers/constantes";
import { DialogConfirm } from "./DialogConfirm";
import {
  SIDEBAR_LOGO_HEIGHT,
  SIDEBAR_WIDTH,
  getCompanyDisplayName,
  getInitials,
  getSidebarColors,
  loginButtonSx,
  useTofuRail,
} from "@helpers/tofuSidebarHelpers";

/**
 * Por ahora el rail solo muestra las tres etapas (Onboarding, Expediente, Firma). Las fases de cada
 * etapa (Cotización, Cuenta, Empresa…) quedan apagadas; para volver a mostrarlas, cambiar a `true`.
 * La configuración de fases sigue en uso: es la que decide en qué etapa va el usuario.
 */
const SHOW_RAIL_PHASES = false;

/** Fila "Guardado automáticamente": apagada hasta que se implemente el autoguardado. */
const SHOW_AUTOSAVE_ROW = false;

export const TofuSidebarContent = () => {
  const navigate = useNavigate();
  const interfaceConfig = useInterfaceConfig();
  const c = getSidebarColors(useTheme());
  const isAuthenticated = useIsAuthenticated();
  const company = useSelector((state: RootState) => state.register.company);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const personalData = useSelector((state: RootState) => state.register.personal_data);

  const userName = [personalData.name, personalData.first_lastname]
    .filter((part) => part?.trim())
    .join(" ")
    .trim();
  const userEmail = accountUser.email?.trim();

  // Mismo cierre de sesión que el Navbar legacy: el canal avisa a las demás pestañas abiertas.
  // Cerrar sesión pide confirmación antes (`DialogConfirm`).
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const { signOutAndRedirect, isSigningOut } = useSignOut();

  const handleSignOut = async () => {
    await signOutAndRedirect();
    setConfirmSignOut(false);
  };

  // Mismo criterio que Navbar: el logo del tenant vive en los recursos estáticos y se oculta
  // hasta que carga, para no dejar el ícono roto mientras llega. Sobre el fondo oscuro se usa la
  // versión para fondo oscuro que manda la config (`lateral.logo_bg_dark`), si existe; sobre el
  // claro, el logo del navbar.
  const [isLogoLoading, setIsLogoLoading] = useState(true);
  const logo = getStaticResourceUrl(
    (c.isDark && interfaceConfig?.lateral?.logo_bg_dark) || interfaceConfig?.navbar.logo,
  );

  const companyName = getCompanyDisplayName(company);
  const companyRfc = company.rfc?.trim();

  const { groups, activeGroupIndex, activeIndex, phases } = useTofuRail();

  return (
    <>
      {/* `.sb-logo` — padding 26px 22px 16px, con la línea inferior de `.sb-dark` */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          pt: "26px",
          px: "22px",
          pb: "16px",
          mb: "4px",
          minHeight: 22,
          borderBottom: `1px solid ${c.divider}`,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="logo"
          onLoad={() => setIsLogoLoading(false)}
          sx={{
            // Alto fijo para todos los tenants: las medidas `navbar.logo_*` son las del navbar legacy
            // y en el sidebar se veían desproporcionadas. El ancho sigue la relación de aspecto y
            // `maxWidth` evita que un logo ancho desborde el sidebar.
            height: SIDEBAR_LOGO_HEIGHT,
            width: "auto",
            maxWidth: "100%",
            objectFit: "contain",
            display: isLogoLoading ? "none" : "block",
          }}
        />
      </Box>

      {/* `.sb-org` — solo con sesión y cuando ya hay una empresa/solicitud en el store. */}
      {isAuthenticated && companyName && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            mx: "16px",
            mt: "18px",
            mb: "22px",
            p: "11px 12px",
            bgcolor: c.org.bg,
            border: `1px solid ${c.org.border}`,
            boxShadow: c.org.shadow,
            borderRadius: "10px",
            cursor: "pointer",
            transition: "background .12s, border-color .12s",
            "&:hover": { bgcolor: c.org.hoverBg, borderColor: c.org.hoverBorder },
          }}
        >
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "7px",
              background: c.org.avatarBg,
              color: c.org.avatarText,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 11, letterSpacing: "0.02em" }}>
              {getInitials(companyName)}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: c.org.name, letterSpacing: "-0.01em" }}>
              {companyName}
            </Typography>
            {companyRfc && (
              <Typography sx={{ fontSize: 11, color: c.org.rfc, fontVariantNumeric: "tabular-nums" }}>
                {companyRfc}
              </Typography>
            )}
          </Box>
          <ChevronRight sx={{ fontSize: 16, color: c.org.chevron, flexShrink: 0 }} />
        </Box>
      )}

      {/* `.sb-rail` — padding 4px 22px 0, gap 26px */}
      <Box
        sx={{
          flex: 1,
          pt: isAuthenticated && companyName ? "4px" : "22px",
          px: "22px",
          display: "flex",
          flexDirection: "column",
          gap: "26px",
        }}
      >
        {groups.map((g, i) => {
          const groupActive = i === activeGroupIndex;
          const groupDone = i < activeGroupIndex;
          return (
            <Box key={g.key}>
              {/* `.sb-rail-group-h` — padding 4px 0 14px, uppercase 11px/600, ls .08em */}
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px", pt: "4px", pb: "14px" }}>
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    border: "1px solid",
                    ...(groupDone && {
                      bgcolor: c.rail.numDoneBg,
                      borderColor: c.rail.numDoneBg,
                      color: c.rail.numDoneText,
                    }),
                    ...(groupActive && {
                      bgcolor: c.rail.numActiveBg,
                      borderColor: c.rail.numActiveBg,
                      color: c.rail.numActiveText,
                    }),
                    ...(!groupDone &&
                      !groupActive && { bgcolor: c.rail.numBg, borderColor: c.rail.numBorder, color: c.rail.numText }),
                    boxShadow: groupActive ? `0 0 0 3px ${c.rail.numActiveRing}` : "none",
                  }}
                >
                  {groupDone ? (
                    <Check sx={{ fontSize: 10 }} />
                  ) : (
                    <Typography component="span" sx={{ fontSize: 10, lineHeight: 1, color: "inherit" }}>
                      {g.num}
                    </Typography>
                  )}
                </Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: groupActive ? c.rail.labelActive : groupDone ? c.rail.labelDone : c.rail.label,
                  }}
                >
                  {g.label}
                </Typography>
              </Box>

              {/* `.sb-rail-list` — pl 8px, línea conectora en left 11px. Apagado por `SHOW_RAIL_PHASES`. */}
              {SHOW_RAIL_PHASES && groupActive && (
                <Box
                  sx={{
                    position: "relative",
                    pl: "8px",
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: "11px",
                      top: "16px",
                      bottom: "16px",
                      width: "1px",
                      bgcolor: c.rail.line,
                    },
                  }}
                >
                  {phases.map((p, phaseIdx) => {
                    const done = phaseIdx < activeIndex;
                    const active = phaseIdx === activeIndex;
                    return (
                      /* `.sb-rail-item` — py 9px, pl 4px, pr 8px, ml -4px, radius 6px */
                      <Box
                        key={p.order}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          py: "9px",
                          pl: "4px",
                          pr: "8px",
                          ml: "-4px",
                          borderRadius: "6px",
                          position: "relative",
                          transition: "color .15s ease, background .15s ease",
                          bgcolor: active ? c.rail.itemActiveBg : "transparent",
                        }}
                      >
                        {/* `.sb-rail-mark` — 16px, borde 1.5px, relleno al completar */}
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                            position: "relative",
                            zIndex: 1,
                            border: "1.5px solid",
                            ...(done && {
                              bgcolor: c.rail.markDoneBg,
                              borderColor: c.rail.markDoneBg,
                              color: "#fff",
                            }),
                            ...(active && {
                              bgcolor: c.rail.markActiveBg,
                              borderColor: c.rail.markActiveBorder,
                            }),
                            ...(!done &&
                              !active && {
                                bgcolor: c.rail.markBg,
                                borderColor: c.rail.markBorder,
                              }),
                          }}
                        >
                          {done ? (
                            <Check sx={{ fontSize: 11 }} />
                          ) : (
                            /* `.sb-rail-dot` — 5px en pendiente, 7px + halo en activo */
                            <Box
                              sx={{
                                width: active ? 7 : 5,
                                height: active ? 7 : 5,
                                borderRadius: "50%",
                                bgcolor: active ? c.rail.dotActive : c.rail.dot,
                                boxShadow: active ? `0 0 0 3px ${c.rail.numActiveRing}` : "none",
                              }}
                            />
                          )}
                        </Box>
                        <Typography
                          sx={{
                            fontSize: 13,
                            letterSpacing: "-0.005em",
                            color: active ? c.rail.itemActiveText : done ? c.rail.itemDoneText : c.rail.itemText,
                            fontWeight: active ? 600 : 500,
                          }}
                        >
                          {p.label}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Sin sesión (cotizador, crear cuenta) no hay nada guardado ni usuario que mostrar. */}
      {isAuthenticated && (
        <>
          {/* `.sb-saved-row` — padding 11px 22px, 11px. Apagado por `SHOW_AUTOSAVE_ROW`. */}
          {SHOW_AUTOSAVE_ROW && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                p: "11px 22px",
                borderTop: `1px solid ${c.divider}`,
              }}
            >
              <CheckCircleOutline sx={{ fontSize: 14, color: c.foot.savedIcon }} />
              <Typography sx={{ fontSize: 11, color: c.foot.savedText }}>Guardado automáticamente</Typography>
            </Box>
          )}

          {/* `.sb-foot` — avatar 30px, nombre 12px/500, correo 11px */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              p: "14px",
              borderTop: `1px solid ${c.divider}`,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                background: c.foot.avatarBg,
                color: c.foot.avatarText,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 600, color: "inherit" }}>
                {getInitials(userName || userEmail || "")}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, lineHeight: 1.3 }}>
              {userName && (
                <Typography noWrap sx={{ fontSize: 12, fontWeight: 600, color: c.foot.name }}>
                  {userName}
                </Typography>
              )}
              {userEmail && (
                <Typography noWrap sx={{ fontSize: 11, color: c.foot.mail }}>
                  {userEmail}
                </Typography>
              )}
            </Box>
            <Tooltip title="Cerrar sesión">
              <IconButton
                size="small"
                onClick={() => setConfirmSignOut(true)}
                data-testid="sidebar-logout-button"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "6px",
                  color: c.foot.iconBtn,
                  "&:hover": { bgcolor: c.foot.iconBtnHoverBg, color: c.foot.iconBtnHoverText },
                }}
              >
                <Logout sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Sin sesión: acceso al login en lugar del pie del usuario. */}
      {!isAuthenticated && (
        <Box sx={{ p: "14px", borderTop: `1px solid ${c.divider}` }}>
          <Button
            variant="outlined"
            fullWidth
            startIcon={<Login sx={{ fontSize: "16px !important" }} />}
            onClick={() => navigate(PATHS_AUTH_V2.login.path)}
            data-testid="sidebar-login-button"
            sx={[loginButtonSx, { height: 40 }]}
          >
            Iniciar sesión
          </Button>
        </Box>
      )}
      <DialogConfirm
        open={confirmSignOut}
        icon={<Logout />}
        title="¿Cerrar sesión?"
        message="Tu avance queda guardado. Para continuar con tu solicitud tendrás que volver a iniciar sesión."
        cancelLabel="Cancelar"
        confirmLabel="Sí, cerrar sesión"
        loadingLabel="Cerrando sesión…"
        loading={isSigningOut}
        onClose={() => setConfirmSignOut(false)}
        onConfirm={handleSignOut}
        cancelTestId="dialog-cerrar-sesion-cancelar-button"
        confirmTestId="dialog-cerrar-sesion-confirmar-button"
      />
    </>
  );
};

/** Sidebar de escritorio: panel fijo a la izquierda. En móvil lo reemplaza `NavbarTofuMobile`. */
export const TofuSidebar = () => (
  <Box
    sx={{
      width: SIDEBAR_WIDTH,
      flex: 1,
      flexShrink: 0,
      display: { xs: "none", md: "flex" },
      flexDirection: "column",
      bgcolor: (theme) => getSidebarColors(theme).bg,
      borderRight: "1px solid",
      borderColor: (theme) => getSidebarColors(theme).border,
    }}
  >
    <TofuSidebarContent />
  </Box>
);
